/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { Command } from 'src/app/model/command';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { DTMFS_DEFAULT_SORT_COLUMN, DTMFS_DEFAULT_SORT_COLUMN_DIRECTION, DTMFS_PAGE_DEFAULT_SIZE, DTMFS_PAGE_SIZE_OPTIONS, DTMFS_TABLE_HEADER } from 'src/app/conf/mtma-dtmfs';
import { MtmaDtmf } from 'src/app/model/mtma-dtmf';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectMtmaActions, selectMtmaActiveTask, selectMtmaCommandAutomation, selectMtmaDtmfModify, selectMtmaDtmfs, selectMtmaDtmfsMaxData, selectMtmaDtmfsPagination } from 'src/app/store/device/device.selector';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { Lang } from 'src/app/model/lang';
import { MtmaTask } from 'src/app/model/mtma-task';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { MtmaDtmfToneDialogComponent } from '../mtma-manage-dtmf-tone-dialog/mtma-manage-dtmf-tone-dialog.component';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';

@Component({
	selector: 'app-mtma-manage-dtmf-tone',
	templateUrl: './mtma-manage-dtmf-tone.component.html',
	providers: [ConfirmationService, DialogService],
})
export class MtmaManageDtmfToneComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaDtmfs$: Observable<MtmaDtmf[] | undefined>;
	mtmaDtmfsSub: Subscription | undefined;
	mtmaDtmfsPagination$: Observable<ItemsPagination | undefined>;
	mtmaDtmfsPaginationSub: Subscription | undefined;
	mtmaDtmfsMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaDtmfsMaxDataSub: Subscription | undefined;
	mtmaCommandAutomation$: Observable<Command[] | undefined>;
	mtmaCommandAutomationSub: Subscription | undefined;
	mtmaActions$: Observable<MtmaAction[] | undefined>;
	mtmaActionsSub: Subscription | undefined;
	mtmaDtmfModify$: Observable<boolean | undefined>;
	mtmaDtmfModifySub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLangSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	mtmaDtmfs: MtmaDtmf[];
	dtmfsLoading: boolean;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	columns = DTMFS_TABLE_HEADER;
	pageSortSub: Subscription;
	paging: PaginationParams = {
		size: DTMFS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: DTMFS_DEFAULT_SORT_COLUMN,
		order: DTMFS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = DTMFS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = DTMFS_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	memorySize = 1;
	maxMemorySize = 1;

	// memoryValue = 0;

	mtmaCommandAutomation?: Command[];

	currentSite: Site;

	mtmaActions: MtmaAction[] = [];
	mtmaActionsSuccessSub: Subscription;
	confirmDeleteSub: Subscription;

	isActiveTask = false;

	/* value = [
		{ label: this.getLabelMemory(), color: '#00B0ED', value: this.getValueMemory(), icon: 'pi cm-memory-storage' }
	]; */

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;
	LG_WIDTH = 1500;

	MAX_MD_WIDTH_SHORT = 858;
	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaDtmfs$ = this.store.select(selectMtmaDtmfs);
		this.mtmaDtmfsPagination$ = this.store.select(selectMtmaDtmfsPagination);
		this.mtmaDtmfsMaxData$ = this.store.select(selectMtmaDtmfsMaxData);
		this.mtmaCommandAutomation$ = this.store.select(selectMtmaCommandAutomation);
		this.mtmaActions$ = this.store.select(selectMtmaActions);
		this.mtmaDtmfModify$ = this.store.select(selectMtmaDtmfModify);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
	}

	ngOnInit(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetDtmfs();
		});
		this.dispatchGetDtmfs();
		this.store.dispatch(DeviceActions.getMtmaActions());

		this.mtmaDtmfsSub = this.mtmaDtmfs$.subscribe(mtmaDtmfs => {
			this.mtmaDtmfs = mtmaDtmfs;
			this.dtmfsLoading = false;
		});

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});

		this.mtmaDtmfsPaginationSub = this.mtmaDtmfsPagination$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.mtmaDtmfsMaxDataSub = this.mtmaDtmfsMaxData$.pipe(filter(p => !!p))
			.subscribe(mtmaDtmfsMaxData => {
				this.memorySize = mtmaDtmfsMaxData.Current;
				this.maxMemorySize = mtmaDtmfsMaxData.Max;
			});

		this.mtmaCommandAutomationSub = this.mtmaCommandAutomation$.subscribe(mtmaCommandAutomation => {
			this.mtmaCommandAutomation = mtmaCommandAutomation;
			this.createTranslationAction();
		});

		this.mtmaActionsSub = this.mtmaActions$.subscribe(mtmaActions => {
			this.mtmaActions = mtmaActions.filter(a => a.DtmfTones && a.Id !== 255);
			this.getAutomationCommand();
			this.createTranslationAction();
		});

		this.currentLangSub = this.currentLang$.subscribe(() => {
			this.store.dispatch(DeviceActions.getMtmaActions());
		});

		this.mtmaDtmfModifySub = this.mtmaDtmfModify$.pipe(filter(mtmaDtmfModify => !!mtmaDtmfModify))
			.subscribe(() => {
				this.dispatchGetDtmfs();
			});

		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});

		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetDtmfs();
			});

		this.onResize();
	}

	ngOnDestroy(): void {
		this.mtmaActionsSub?.unsubscribe();
		this.mtmaCommandAutomationSub?.unsubscribe();
		this.currentLangSub?.unsubscribe();
		this.mtmaDtmfModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.mtmaDtmfsMaxDataSub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
	}

	/* getLabelMemory(): string {
		return this.translate.instant('MEMORY_USAGE') + ' ' + this.memorySize + ' / ' + this.maxMemorySize;
	} */

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	loadDtmfs(event: TableLazyLoadEvent) {
		this.dtmfsLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.dispatchGetDtmfs();
	}

	dispatchGetDtmfs(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaDtmfs({
				filter: this.filter,
				sort: this.paging.sort, order: this.paging.order, size: this.paging.size, page: this.paging.page
			}));
		}
	}

	isMtmaConnect(): boolean {
		//return (this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.MTMACONNECT) && this.tabIndex === 9); // TAB INDEX FOR MTMACONNECT CALLLOG IS 9
		return (this.tabIndex === 4); // TAB INDEX FOR MTMACONNECT IS 4 tab only for mtma

	}

	isSelectTab(): boolean {
		return this.isMtmaConnect();
	}



	addDtmf(): void {
		this.store.dispatch(DeviceActions.resetDtmfForm());
		this.ref = this.dialogService.open(MtmaDtmfToneDialogComponent, {
			header: this.translate.instant('ADD_DTMF'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editDtmf(dtmfTone: MtmaDtmf): void {
		this.store.dispatch(DeviceActions.resetDtmfForm());
		this.ref = this.dialogService.open(MtmaDtmfToneDialogComponent, {
			header: this.translate.instant('EDIT_DTMF'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				dtmfTone
			},
		});
	}

	createTranslationAction(): void {
		if (this.mtmaActions && this.mtmaCommandAutomation) {
			for (const action of this.mtmaActions) {
				if (action.AutomationId) {
					action.Traslation = this.getTranslationAction(action);
				} else {
					action.Traslation = this.translate.instant(action.Description);
				}
			}
		}
	}

	getTranslationAction(action: MtmaAction): string {
		for (const command of this.mtmaCommandAutomation) {
			let nameDevice = '';
			if (this.currentSite && this.currentSite.Devices) {
				for (const device of this.currentSite.Devices) {
					if (device.Id === command.AutomationId) {
						nameDevice = device.Name + ' - ';
					}
				}
			}
			if (action.AutomationId === command.AutomationId && action.Command === command.Id) {
				return nameDevice + command.Description;
			}
		}
		return '';
	}

	async getAutomationCommand(): Promise<void> {
		const mapAutomation = new Map<number, number>();
		for (const action of this.mtmaActions) {
			if (action.AutomationId && !mapAutomation.has(action.AutomationId)) {
				mapAutomation.set(action.AutomationId, action.AutomationId);
			}
		}
		let cont = 0;
		for (const key of mapAutomation.keys()) {
			setTimeout(() => {
				this.store.dispatch(DeviceActions.getMtmaAutomationCommands({ deviceId: key }));
			}, cont * 1000);
			cont++;
		}
	}

	getTextAction(dtmf: MtmaDtmf): string {
		let text = '';
		if (dtmf.Action !== undefined) {
			for (const actionAll of this.mtmaActions) {
				if (dtmf.Action === actionAll.Id) {
					if (text === '') {
						text = actionAll.Traslation;
					} else {
						text = text + ', ' + actionAll.Traslation;
					}
					break;
				}
			}
		}
		return text;
	}

	delete(dtmf: MtmaDtmf): void {
		/*const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			minWidth: '20vw',
			maxWidth: '50vw',
			data: { title: 'DELETE_DTMF', body: 'DELETE_DTMF_WARNING' }
		});
		this.confirmDeleteSub = dialogRef.afterClosed().subscribe(result => {
			if (result === 'success') {
				this.store.dispatch(DeviceActions.deleteMtmaDtmf({ dtmfId:dtmf.ObjId }));
			}
		});*/
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_DTMF'),
			message: this.translate.instant('DELETE_DTMF_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaDtmf({ dtmfId: dtmf.ObjId }));
			},
			reject: () => {
			}
		});
	}

	availableActions(): boolean {
		return this.mtmaDtmfs.filter(ac => ac.Action !== 255)?.length >= this.mtmaActions.length;
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}

